import React, {useEffect, useState} from 'react';
import getWithAuth from "../../context/getWithAuth";
import Loader from "../../components/loader";
import ToSModal from "../../components/tosModal";
import InteractiveSearch from "../../components/interactiveSearch";
import StudentOverviewPieChart from "./studentOverviewPieChart";
import StudentCourseActiveDataButton from "./studentCourseActiveDataButton";
import StudentCourseArchiveDataButton from "./studentCourseArchiveDataButton";
import StudentPrepCalendarModal from "./studentPrepCalendarModal";
import LocalLoaderLight from "../../components/localLoaderLight";
import QuestionPreviewModal from "../../student/myData/questionPreviewModal";
import DOMPurify from "dompurify";

function StudentOverview({user}) {
    const [studentOverviewData, setStudentOverviewData] = useState(null);
    const [questionPreviewModal, setQuestionPreviewModal] = useState(null);
    const [studentList, setStudentList] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [studentLoading, setStudentLoading] = useState(false);
    const [studentFilterText, setStudentFilterText] = useState(null);
    const [studentPrepCalendarModal, setStudentPrepCalendarModal] = useState(null);
    const [pieChartData, setPieChartData] = useState(null);
    const [query, setQuery] = useState(null);

    const renderHtml = (htmlString) => {
        const sanitizedHtml = DOMPurify.sanitize(htmlString);
        return { __html: sanitizedHtml };
    };

    const handleFilterChange = (e) => {
        setStudentFilterText(e.target.value);
        setQuery(e.target.value)
    }

    const handleStudentClick = (student) => {
        setStudentOverviewData(null);
        setStudentLoading(true);
        getStudentOverview(student.studentId)
    }

    const getLocalStudentList = async (user) => {
        if (user.userAttributes) {
            try {
                getWithAuth('/data/getLocalStudentList', user.userAttributes['custom:school'], {
                    userId: user.userContext._id.$oid,
                })
                    .then(async r => {
                        setStudentList(r);
                    })
                    .then(() => setIsLoading(false))
            } catch (error) {
                console.error('Error fetching standards data:', error)
            }
        }
    }

    const getStudentOverview = async (student) => {
        if (user.userAttributes) {
            await setStudentLoading(true);
            try {
                getWithAuth('/data/getStudentOverview', user.userAttributes['custom:school'], {
                    studentId: student,
                })
                    .then(async r => {
                        setStudentOverviewData(r);
                        setPieChartData(
                            [
                                {
                                    name: "Correct",
                                    value: (Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').length > 0) ? Object.values(r.studentData.active).flat().filter(entry => (entry.result === true || typeof entry.result === 'number' && entry.result === 0)).length + Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').flatMap(entry => entry.recallItems).map(entry => entry.active).filter(entry => entry.correct === true).length / Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').flatMap(entry => entry.recallItems).map(entry => entry.active).length : Object.values(r.studentData.active).flat().filter(entry => (entry.result === true || typeof entry.result === 'number' && entry.result === 0)).length,
                                    fill: "#538ea5"
                                },
                                {
                                    name: 'Pending',
                                    value: (Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').length > 0) ? Object.values(r.studentData.active).flat().filter(entry => (entry.result === 'pending')).length + Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').flatMap(entry => entry.recallItems).map(entry => entry.active).filter(entry => entry.correct === 'pending').length / Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').flatMap(entry => entry.recallItems).map(entry => entry.active).length : Object.values(r.studentData.active).flat().filter(entry => (entry.result === 'pending')).length,
                                    fill: "#8b8b8b"
                                },
                                {
                                    name: 'Incorrect',
                                    value: (Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').length > 0) ? Object.values(r.studentData.active).flat().filter(entry => (entry.result === false || typeof entry.result === 'number' && entry.result > 0)).length + Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').flatMap(entry => entry.recallItems).map(entry => entry.active).filter(entry => entry.correct === false).length / Object.values(r.studentData.active).flat().filter(entry => entry.type === 'Recall').flatMap(entry => entry.recallItems).map(entry => entry.active).length : Object.values(r.studentData.active).flat().filter(entry => (entry.result === false || typeof entry.result === 'number' && entry.result > 0)).length,
                                    fill: "#dd6e42"
                                }
                            ]
                        )
                    })
                    .then(() => setStudentLoading(false))
            } catch (error) {
                console.error('Error fetching standards data:', error)
            }
        }
    }

    useEffect(() => {
        getLocalStudentList(user)
    }, []);

    if (isLoading) {
        return <Loader message={'Fetching student list'}/>
    } else {
        return (
            <div className={'min-h-screen flex flex-col'}>
                <div className={'grid grid-cols-7 grow'}>
                    <div className={'bg-platinum-900 rounded-lg p-1 col-span-2 h-screen'}>
                        <div>
                            <label htmlFor="student-lookup-search"
                                   className="mb-2 text-sm font-medium text-gray-900 sr-only dark:text-white">Search</label>
                            <div className="relative">
                                <div
                                    className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <svg aria-hidden="true" className="w-5 h-5 text-gray-500 dark:text-gray-400"
                                         fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                         xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                    </svg>
                                </div>
                                <input type="search" id="default-search" onChange={handleFilterChange}
                                       value={query}
                                       className="block w-full p-2 mb-2 pl-10 text-xs text-gray-900 border border-gray-300 rounded-lg bg-dutch_white-100 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                                       placeholder={"Search for Students"} title={"Student Search"}/>
                            </div>
                        </div>
                        <div
                            className={'bg-platinum-900 p-4 flex flex-col overflow-y-auto gap-1 max-h-[93%] w-full rounded text-sm'}>

                            {(studentFilterText) ? <>
                                {studentList.filter(student => student.firstName.toLowerCase().includes(studentFilterText.toLowerCase()) || student.lastName.toLowerCase().includes(studentFilterText.toLowerCase())).sort((a, b) => a.lastName.localeCompare(b.lastName)).map(student =>
                                    <button id={student.studentId}
                                            className={'p-2 bg-columbia_blue rounded h-fit select-none hover:bg-columbia_blue-600 cursor-pointer'}
                                            onClick={() => handleStudentClick(student)}>
                                        <p id={student.studentId}>{student.firstName} {student.lastName}</p>
                                    </button>)}
                            </>
                                : <>
                                    {studentList.sort((a, b) => a.lastName.localeCompare(b.lastName)).map(student =>
                                        <button id={student.studentId}
                                                className={'p-2 bg-columbia_blue rounded h-fit select-none hover:bg-columbia_blue-600 cursor-pointer'}
                                                onClick={() => handleStudentClick(student)}>
                                            <p id={student.studentId}>{student.firstName} {student.lastName}</p>
                                        </button>)}</>
                            }
                        </div>
                    </div>
                    {(studentLoading) ? <div className={'col-span-5 text-dutch_white-200 my-auto'}>
                            <LocalLoaderLight message={'Gathering student data'} />
                        </div>
                        : (studentOverviewData && pieChartData) ?
                            <div className={'col-span-5'}>
                                <div className={'flex rounded p-2 m-1 bg-dutch_white-200 text-columbia_blue-900 w-full'}>
                                    <div className={'w-[50%] flex flex-col'}>
                                        <p className={'text-xl font-bold'}>{studentOverviewData.studentData.firstName} {studentOverviewData.studentData.lastName}</p>
                                        <div className={'bg-columbia_blue-300 grow rounded overflow-y-auto flex flex-col'}>
                                            <p className={'text-sm'}>Most recent learning:</p>
                                            {Object.values(studentOverviewData.studentData.active).flat().filter(item => Object.keys(item).includes('dateAssigned')).sort((a, b) => new Date(b.dateAssigned.$date) - new Date(a.dateAssigned.$date)).slice(0, 5).map(item => (item.result === true || item.result === 0) ?
                                                <button
                                                className={'bg-columbia_blue-600 hover:bg-columbia_blue-700 cursor-pointer p-2 m-1 text-xs rounded shadow text-columbia_blue-900 text-left w-fit'}
                                            onClick={() => setQuestionPreviewModal(item.questionTemplateId)}
                                            aria-label={`Element: ${studentOverviewData.standardElements.find(entry => entry._id.$oid === item.knowShowId).text}. Most recent performance: Correct`}>
                                            <div
                                                dangerouslySetInnerHTML={renderHtml(studentOverviewData.standardElements.find(entry => entry._id.$oid === item.knowShowId).text)}/>
                                        </button>
                                        : (item.result === 'pending') ?
                                        <div
                                            className={'bg-platinum-600 p-2 m-1 rounded shadow text-columbia_blue-900 text-xs text-left w-fit'}
                                            tabIndex={0}
                                            aria-label={`Element: ${studentOverviewData.standardElements.find(entry => entry._id.$oid === item.knowShowId).text}. Most recent performance: Pending`}
                                            dangerouslySetInnerHTML={renderHtml(studentOverviewData.standardElements.find(entry => entry._id.$oid === item.knowShowId).text)}/>
                                        :
                                        <button
                                            className={'bg-burnt_sienna-400 hover:bg-burnt_sienna-300 cursor-pointer p-2 m-1 rounded shadow text-columbia_blue-900 text-xs text-left w-fit'}
                                            onClick={() => setQuestionPreviewModal(item.questionTemplateId)}
                                            aria-label={`Element: ${studentOverviewData.standardElements.find(entry => entry._id.$oid === item.knowShowId).text}. Most recent performance: Incorrect`}>
                                            <div
                                                dangerouslySetInnerHTML={renderHtml(studentOverviewData.standardElements.find(entry => entry._id.$oid === item.knowShowId).text)}/>
                                        </button>)}

                                        </div>
                                    </div>
                                    <div className={'grow'} />
                                    <div className={'p-2 w-[50%] flex-col'}>
                                        <p className={'font-bold text-lg'}>Overall Performance</p>
                                        <StudentOverviewPieChart data={pieChartData} />
                                    </div>
                                </div>

                                <div className={'grid grid-cols-2 ml-2 gap-2'}>
                                    <div className={'rounded-lg border-2 border-dutch_white-100'}>
                                        <p className={'text-lg text-dutch_white-200'}>Active Classes</p>
                                        {Object.keys(studentOverviewData.studentData.active).filter(prep => studentOverviewData.activeRosters.includes(prep)).map(prep => <StudentCourseActiveDataButton selectFunction={() => setStudentPrepCalendarModal(prep)} prep={prep} studentData={studentOverviewData.studentData} />)}
                                    </div>
                                    <div className={'rounded-lg border-2 border-dutch_white-100'}>
                                        <p className={'text-lg text-dutch_white-200'}>Archived Classes</p>
                                        {Object.keys(studentOverviewData.studentData.active).filter(prep => !studentOverviewData.activeRosters.includes(prep) && studentOverviewData.studentData.active[prep].length > 0).map(prep => <StudentCourseArchiveDataButton selectFunction={() => setStudentPrepCalendarModal(prep)} prep={prep} studentData={studentOverviewData.studentData} />)}
                                    </div>
                                </div>
                            </div>
                        :
                        <div className={'col-span-5 text-dutch_white-200 mt-8'}>
                            Use the search to find and select a student
                        </div>
                    }
                </div>
                {(studentPrepCalendarModal && <StudentPrepCalendarModal user={user} studentData={studentOverviewData.studentData} selectedPrep={studentPrepCalendarModal} setStudentPrepCalendar={setStudentPrepCalendarModal} standardElements={studentOverviewData.standardElements}/>)}
                {(questionPreviewModal) &&
                    <QuestionPreviewModal user={user} questionPreviewModal={questionPreviewModal}
                                          setQuestionPreviewModal={setQuestionPreviewModal}/>}
            </div>
        );
    }
}

export default StudentOverview;